import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";
import { toast } from "react-toastify";

const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_IMAGE_SIZE = 200 * 1024;

const getAccessToken = () => localStorage.getItem("accessToken");

const getAuthConfig = () => {
  const token = getAccessToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// ==============================================================
// HELPERS
// ==============================================================
// Pulls a readable message out of a FastAPI / axios error
const errMsg = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ");
  return e?.message || fallback;
};

// guide text is stored as Quill HTML
const htmlToText = (html = "") => {
  if (!html) return "";
  const spaced = html.replace(/<\/(p|li|h[1-6]|blockquote|div)>/gi, " $&");
  const doc = new DOMParser().parseFromString(spaced, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

// Light client-side cleanup. Use DOMPurify in production and sanitize on the server too.
const sanitizeHtml = (html = "") => {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const decodedHtml =
    parsed.body.children.length === 0 &&
    /&lt;\/?[a-z][\s\S]*?&gt;/i.test(parsed.body.textContent || "")
      ? parsed.body.textContent
      : html;
  const doc = new DOMParser().parseFromString(decodedHtml, "text/html");
  doc
    .querySelectorAll("script,style,iframe,object,embed,link,meta")
    .forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((a) => {
      if (/^on/i.test(a.name) || /^\s*javascript:/i.test(a.value))
        el.removeAttribute(a.name);
    });
  });
  return doc.body.innerHTML;
};

const requestGuideImageUpload = async (file) => {
  const contentType = file.type || "application/octet-stream";
  const { data } = await axios.post(
    `${BASE_URL}/guide/upload-url`,
    {
      filename: file.name,
      content_type: contentType,
    },
    getAuthConfig(),
  );

  if (!data?.upload_url || !data?.image_key) {
    throw new Error("Could not prepare the image upload.");
  }

  return { uploadUrl: data.upload_url, imageKey: data.image_key, contentType };
};

const uploadGuideImage = async (file, upload) => {
  if (!upload?.uploadUrl || !upload?.imageKey) {
    throw new Error("The image upload is not ready. Select the image again.");
  }

  await axios.put(upload.uploadUrl, file, {
    headers: { "Content-Type": upload.contentType },
  });

  return upload.imageKey;
};

const getGuideImageKey = (image) => {
  if (!image) return "";

  try {
    const url = new URL(image);
    if (url.hostname.endsWith(".amazonaws.com")) {
      return decodeURIComponent(url.pathname.replace(/^\/+/, ""));
    }
  } catch {
    return image;
  }

  return image;
};

// ==============================================================
// THEME TOKENS
// ==============================================================
const getTokens = (dark) => {
  const t = {
    page: dark ? "#171923" : "#f8fafc",
    card: dark ? "#1a202c" : "#ffffff",
    subtle: dark ? "#171923" : "#f8fafc",
    border: dark ? "#2d3748" : "#e2e8f0",
    text: dark ? "#e2e8f0" : "#1a202c",
    muted: dark ? "#a0aec0" : "#718096",
    accent: dark ? "#63b3ed" : "#2563eb",
    accentBg: dark ? "rgba(99,179,237,0.14)" : "#eff6ff",
    okText: dark ? "#68d391" : "#047857",
    okBg: dark ? "rgba(72,187,120,0.14)" : "#ecfdf5",
    warnText: dark ? "#f6ad55" : "#b45309",
    warnBg: dark ? "rgba(237,137,54,0.14)" : "#fffbeb",
  };
  t.bd = `1px solid ${t.border}`;
  t.eyebrow = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: t.muted,
  };
  return t;
};

// ==============================================================
// MODAL SHELL + SUBMIT BUTTON
// ==============================================================
const ModalShell = ({
  t,
  open,
  onClose,
  icon,
  title,
  subtitle,
  maxWidth = "sm",
  width,
  footer,
  children,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth={width ? false : maxWidth}
    PaperProps={{
      sx: {
        borderRadius: 3,
        backgroundColor: t.card,
        backgroundImage: "none",
        border: t.bd,
        ...(width && { width, maxWidth: "calc(100% - 32px)" }),
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 3,
        py: 2.25,
        borderBottom: t.bd,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: t.accent,
          backgroundColor: t.accentBg,
        }}
      >
        <Icon icon={icon} width={20} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.3, color: t.text }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography noWrap variant="body2" sx={{ color: t.muted }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <IconButton size="small" aria-label="Close" onClick={onClose}>
        <Icon icon="lucide:x" width={18} />
      </IconButton>
    </Box>

    <Box sx={{ px: 3, py: 2.5, overflowY: "auto" }}>{children}</Box>

    {footer && (
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          borderTop: t.bd,
          backgroundColor: t.subtle,
        }}
      >
        {footer}
      </Box>
    )}
  </Dialog>
);

const SubmitButton = ({ loading, disabled, onClick, children }) => (
  <Button
    variant="contained"
    disableElevation
    disabled={disabled || loading}
    onClick={onClick}
    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 2.5 }}
  >
    {children}
  </Button>
);

// ==============================================================
// EMPTY / ERROR STATE
// ==============================================================
const EmptyState = ({ t, icon, title, hint, action }) => (
  <Box sx={{ py: 7, px: 2, textAlign: "center" }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        mx: "auto",
        mb: 2,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: t.accent,
        backgroundColor: t.accentBg,
      }}
    >
      <Icon icon={icon} width={28} />
    </Box>
    <Typography sx={{ fontWeight: 600, fontSize: 16, color: t.text }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{ mt: 0.5, mx: "auto", maxWidth: 360, color: t.muted }}
    >
      {hint}
    </Typography>
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const ErrorBanner = ({ message, onRetry }) => (
  <Alert
    severity="error"
    action={
      onRetry && (
        <Button
          color="inherit"
          size="small"
          onClick={onRetry}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Retry
        </Button>
      )
    }
  >
    {message}
  </Alert>
);

const GuideImageStrip = ({ t, images, renderImage }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const imagesPerPage = 3;
  const totalPages = Math.ceil(images.length / imagesPerPage);
  const startIndex = currentPage * imagesPerPage;
  const visibleImages = images.slice(startIndex, startIndex + imagesPerPage);

  useEffect(() => {
    setCurrentPage(0);
  }, [images]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 2,
        width: "100%",
      }}
    >
      {visibleImages.map((image, index) => (
        <Box key={image.id || image.src || startIndex + index} sx={{ minWidth: 0 }}>
          {renderImage(image, startIndex + index)}
        </Box>
      ))}
      {totalPages > 1 && (
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            size="small"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((page) => page - 1)}
            sx={{ textTransform: "none" }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: t.muted }}>
            Page {currentPage + 1} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage((page) => page + 1)}
            sx={{ textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ==============================================================
// GUIDE CARD + SKELETON
// ==============================================================
const GuideCard = ({ t, guide, view, onPreview, onEdit, onDelete }) => {
  const list = view === "list";
  const snippet = useMemo(() => htmlToText(guide.text), [guide.text]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: list ? { xs: "column", sm: "row" } : "column",
        overflow: "hidden",
        borderRadius: 2,
        border: t.bd,
        backgroundColor: t.card,
      }}
    >
      {guide.images?.[0] && (
        <Box
          component="img"
          src={guide.images[0]}
          alt={guide.title}
          sx={{
            width: list ? { xs: "100%", sm: 240 } : "100%",
            height: list ? { xs: 160, sm: "auto" } : 170,
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ p: 2.5, flexGrow: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              lineHeight: 1.4,
              color: t.text,
            }}
          >
            {guide.title}
          </Typography>

          {snippet && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: t.muted,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {snippet}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: t.bd,
            backgroundColor: t.subtle,
          }}
        >
          <Button
            size="small"
            startIcon={<Icon icon="lucide:eye" width={16} />}
            onClick={() => onPreview(guide)}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Preview guide
          </Button>
          <Box>
            <Tooltip title="Edit guide">
              <IconButton
                size="small"
                aria-label="Edit guide"
                onClick={() => onEdit(guide)}
              >
                <Icon icon="lucide:pencil" width={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete guide">
              <IconButton
                size="small"
                aria-label="Delete guide"
                onClick={() => onDelete(guide.id)}
              >
                <Icon icon="lucide:trash-2" width={17} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const GuideCardSkeleton = ({ t, view }) => {
  const list = view === "list";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: list ? { xs: "column", sm: "row" } : "column",
        overflow: "hidden",
        borderRadius: 2,
        border: t.bd,
        backgroundColor: t.card,
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          width: list ? { xs: "100%", sm: 240 } : "100%",
          height: list ? 150 : 170,
          flexShrink: 0,
        }}
      />
      <Box sx={{ p: 2.5, flexGrow: 1 }}>
        <Skeleton variant="text" height={26} width="90%" />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="75%" />
      </Box>
    </Box>
  );
};

// ==============================================================
// OPTION MODAL
// ==============================================================
const MAX_DESC = 120;

const OptionDialog = ({
  t,
  open,
  onClose,
  editing,
  roleLabel,
  roleId,
  onSaved,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (!open) return;

    setName(editing?.name || "");
    setDescription(editing?.description || "");
    setNameError("");
  }, [open, editing]);

  const trimmed = name.trim();

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const getApiErrorMessage = (error) => {
    const detail = error?.response?.data?.detail;

    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      return detail
        .map((item) => item?.msg || item?.detail || String(item))
        .join(", ");
    }

    if (typeof error?.response?.data?.message === "string") {
      return error.response.data.message;
    }

    if (error?.message) {
      return error.message;
    }

    return "Could not save the option.";
  };

  const submit = async () => {
    if (saving) return;

    // Frontend validation
    if (!trimmed) {
      setNameError("Option name is required.");
      return;
    }

    // Remove any previous validation error
    setNameError("");

    setSaving(true);

    try {
      const optionData = {
        option_name: trimmed,
        description: description.trim(),
      };

      let optionId = editing?.id;

      if (editing) {
        await axios.put(
          `${BASE_URL}/guide/options/${editing.id}`,
          optionData,
          getAuthConfig(),
        );
      } else {
        const { data } = await axios.post(
          `${BASE_URL}/guide/options`,
          {
            role_id: roleId,
            ...optionData,
          },
          getAuthConfig(),
        );

        optionId = data?.id;
      }

      await onSaved(optionId, !editing);

      toast.success(editing ? "Option updated" : "Option added");
    } catch (e) {
      const message = getApiErrorMessage(e);

      /*
       * Keep the original FastAPI error message.
       *
       * If backend returns a duplicate-option error,
       * show that same message on the input as well.
       */
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      const isDuplicateError =
        status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already exists");

      if (isDuplicateError) {
        setNameError(message);
      }

      // Always preserve the backend error in toast
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      t={t}
      open={open}
      onClose={close}
      maxWidth="xs"
      icon="lucide:folder-plus"
      title={editing ? "Edit option" : "Add option"}
      subtitle={`Topic under the ${roleLabel} role`}
      footer={
        <>
          <Button
            onClick={close}
            disabled={saving}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <SubmitButton loading={saving} onClick={submit}>
            {editing ? "Save changes" : "Add option"}
          </SubmitButton>
        </>
      }
    >
      <Stack spacing={2.5}>
        {/* Option Name */}
        <Box>
          <Typography
            component="label"
            sx={{
              display: "block",
              mb: 0.7,
              fontSize: "0.82rem",
              fontWeight: 500,
              color: nameError ? "error.main" : "text.primary",
            }}
          >
            Option name
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Login issue"
            value={name}
            onChange={(e) => {
              setName(e.target.value);

              // Clear backend/frontend validation
              // once the user starts correcting it.
              if (nameError) {
                setNameError("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            error={Boolean(nameError)}
            helperText={nameError || " "}
            FormHelperTextProps={{
              sx: {
                ml: 0,
                minHeight: "20px",
              },
            }}
          />
        </Box>

        {/* Description */}
        <Box>
          <Typography
            component="label"
            sx={{
              display: "block",
              mb: 0.7,
              fontSize: "0.82rem",
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            Short description{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 400,
                color: "text.secondary",
              }}
            >
              (optional)
            </Box>
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            placeholder="What kind of issues do the guides here cover?"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
            helperText={`${description.length}/${MAX_DESC}`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                color: t.muted,
                mr: 0,
              },
            }}
          />
        </Box>
      </Stack>
    </ModalShell>
  );
};

// ==============================================================
// GUIDE MODAL  (create | edit | preview)
// ==============================================================
const EMPTY = {
  optionId: "",
  title: "",
  text: "",
  images: [],
};

const toForm = (g) => ({
  optionId: g.optionId,
  title: g.title || "",
  text: g.text || "",
  images: (Array.isArray(g.images) ? g.images : g.image ? [g.image] : []).map(
    (image, index) => ({
      id: `existing-${index}-${image}`,
      preview: image,
      imageKey: getGuideImageKey(image),
      file: null,
      upload: null,
      loading: false,
      error: "",
    }),
  ),
  status: g.status || "Draft",
});

const Section = ({ t, title, hint, children }) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        mb: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: 13, color: t.text }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: t.muted }}>
          {hint}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "color",
  "background",
  "list",
  "bullet",
  "indent",
  "align",
  "link",
];

// ==============================================================
// GUIDE MODAL  (create | edit | preview)
// ==============================================================

const GuideDialogInner = ({
  t,
  open,
  onClose,
  mode = "create", // "create" | "edit" | "preview"
  guide,
  options,
  defaultOptionId,
  onSaved,
  onEditRequest,
}) => {
  const [form, setForm] = useState(() =>
    mode === "create" || !guide
      ? { ...EMPTY, optionId: defaultOptionId || "" }
      : toForm(guide),
  );

  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [titleError, setTitleError] = useState("");

  const blobRefs = useRef(new Map());
  const imageUploadRequestRef = useRef(0);

  const isPreview = mode === "preview";
  const isEdit = mode === "edit";

  // Free the blob preview when the dialog session ends
  useEffect(
    () => () => {
      blobRefs.current.forEach((url) => URL.revokeObjectURL(url));
      blobRefs.current.clear();
    },
    [],
  );

  const set = (key, value) => {
    setForm((p) => ({
      ...p,
      [key]: value,
    }));
  };

  // Quill returns "<p><br></p>" when empty,
  // so check the plain text instead.
  const plainText = useMemo(
    () => htmlToText(form.text),
    [form.text],
  );

  const optionName = options.find(
    (o) => o.id === form.optionId,
  )?.name;

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const takeFiles = (files) => {
    if (isPreview || !files?.length) return;

    const selectedFiles = Array.from(files);
    const validTypeFiles = selectedFiles.filter((file) => {
      const mimeType = file.type.toLowerCase();
      const extension = file.name.split(".").pop()?.toLowerCase();
      return (
        ALLOWED_IMAGE_TYPES.has(mimeType) &&
        ALLOWED_IMAGE_EXTENSIONS.has(extension)
      );
    });
    const validFiles = validTypeFiles.filter(
      (file) => file.size <= MAX_IMAGE_SIZE,
    );
    const rejectionMessages = [];

    if (validTypeFiles.length < selectedFiles.length) {
      rejectionMessages.push(
        "Only JPG, JPEG, PNG, and WebP images are allowed.",
      );
    }
    if (validFiles.length < validTypeFiles.length) {
      rejectionMessages.push("Images must be 200 KB or smaller.");
    }

    if (rejectionMessages.length) {
      const errorMessage = rejectionMessages.join(" ");
      toast.error(errorMessage);
      setForm((p) => ({ ...p, imageUploadError: errorMessage }));
    } else if (validFiles.length) {
      setForm((p) => ({ ...p, imageUploadError: "" }));
    }
    if (!validFiles.length) return;

    validFiles.forEach(async (file) => {
      const id = `upload-${++imageUploadRequestRef.current}`;
      const preview = URL.createObjectURL(file);
      blobRefs.current.set(id, preview);
      setForm((p) => ({
        ...p,
        images: [
          ...p.images,
          { id, preview, file, upload: null, loading: true, error: "" },
        ],
      }));

      try {
        const upload = await requestGuideImageUpload(file);
        setForm((p) => ({
          ...p,
          images: p.images.map((image) =>
            image.id === id ? { ...image, upload, loading: false } : image,
          ),
        }));
      } catch (error) {
        setForm((p) => ({
          ...p,
          images: p.images.map((image) =>
            image.id === id
              ? {
                  ...image,
                  loading: false,
                  error: errMsg(error, "Could not prepare the image upload."),
                }
              : image,
          ),
        }));
      }
    });
  };

  const removeImage = (imageId) => {
    const preview = blobRefs.current.get(imageId);
    if (preview) {
      URL.revokeObjectURL(preview);
      blobRefs.current.delete(imageId);
    }
    setForm((p) => ({
      ...p,
      images: p.images.filter((image) => image.id !== imageId),
      imageUploadError: "",
    }));
  };

  const getApiErrorMessage = (error) => {
    const detail =
      error?.response?.data?.detail;

    // FastAPI HTTPException detail
    if (typeof detail === "string") {
      return detail;
    }

    // FastAPI validation error
    if (Array.isArray(detail)) {
      return detail
        .map(
          (item) =>
            item?.msg ||
            item?.detail ||
            String(item),
        )
        .join(", ");
    }

    // Other backend response format
    if (
      typeof error?.response?.data?.message ===
      "string"
    ) {
      return error.response.data.message;
    }

    // Axios error
    if (error?.message) {
      return error.message;
    }

    return isEdit
      ? "Could not update the guide."
      : "Could not create the guide.";
  };

  const isTitleApiError = (error, message) => {
    const status = error?.response?.status;

    if (!message) {
      return false;
    }

    const lowerMessage =
      message.toLowerCase();

    /*
     * Backend is the source of truth.
     *
     * We only put the backend error under the
     * title field when the backend message indicates
     * that the problem is related to the title.
     */
    return (
      status >= 400 &&
      status < 500 &&
      (
        lowerMessage.includes("title") ||
        lowerMessage.includes("guide title") ||
        lowerMessage.includes("already exists")
      )
    );
  };

  const submit = async () => {
    if (saving || isPreview) {
      return;
    }

    const trimmedTitle =
      form.title.trim();

    // Only frontend validation.
    if (!trimmedTitle) {
      setTitleError(
        "Guide title is required.",
      );
      return;
    }

    // Remove previous title validation error
    // before making the API request.
    setTitleError("");

    if (form.images.some((image) => image.loading || image.error)) {
      toast.error("Wait for all images to finish uploading, or remove failed images.");
      return;
    }

    setSaving(true);

    try {
      const imageKeys = await Promise.all(
        form.images.map((image) =>
          image.file
            ? uploadGuideImage(image.file, image.upload)
            : image.imageKey || getGuideImageKey(image.preview),
        ),
      );
      const guideData = {
        ...(isEdit ? {} : { option_id: Number(form.optionId) }),
        title: trimmedTitle,
        text: plainText ? form.text : null,
        image: imageKeys.filter(Boolean),
      };

      if (isEdit) {
        await axios.put(
          `${BASE_URL}/guide/guides/${guide.id}`,
          guideData,
          getAuthConfig(),
        );
      } else {
        await axios.post(
          `${BASE_URL}/guide/guides`,
          guideData,
          getAuthConfig(),
        );
      }

      await onSaved(form.optionId);

      toast.success(
        isEdit
          ? "Guide updated"
          : "Guide created",
      );
    } catch (e) {
      const message =
        getApiErrorMessage(e);

      /*
       * If FastAPI says the problem is with
       * the title, use the ORIGINAL backend
       * message for the field as well.
       */
      if (
        isTitleApiError(e, message)
      ) {
        setTitleError(message);
      }

      // Always preserve the original API
      // response in the toast.
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const titles = {
    create: "Create guide",
    edit: "Edit guide",
    preview: "Guide preview",
  };

  const subtitles = {
    create: "Add text, an image, or both.",
    edit: "Update the text, image, or both.",
    preview: "This is how the guide will appear.",
  };

  const quillSx = {
    "& .ql-toolbar.ql-snow": {
      border: t.bd,
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      backgroundColor: t.subtle,
    },

    "& .ql-container.ql-snow": {
      border: t.bd,
      borderTop: "none",
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      backgroundColor: t.card,
      fontSize: 14,
    },

    "& .ql-editor": {
      minHeight: 220,
      maxHeight: 380,
      overflowY: "auto",
      color: t.text,
    },

    "& .ql-editor.ql-blank::before": {
      color: t.muted,
      fontStyle: "normal",
    },

    "& .ql-snow .ql-stroke": {
      stroke: t.muted,
    },

    "& .ql-snow .ql-fill": {
      fill: t.muted,
    },

    "& .ql-snow .ql-picker": {
      color: t.muted,
    },

    "& .ql-snow .ql-picker-options": {
      backgroundColor: t.card,
      border: t.bd,
    },

    "& .ql-snow button:hover .ql-stroke, & .ql-snow .ql-picker-label:hover .ql-stroke":
      {
        stroke: t.accent,
      },

    "& .ql-snow button:hover .ql-fill": {
      fill: t.accent,
    },
  };

  return (
    <ModalShell
      t={t}
      open={open}
      onClose={close}
      width={{
        xs: "100%",
        md: "80vw",
        lg: "50vw",
      }}
      icon={
        isPreview
          ? "lucide:eye"
          : "lucide:book-open-text"
      }
      title={titles[mode]}
      subtitle={subtitles[mode]}
      footer={
        isPreview ? (
          <>
            <Button
              onClick={close}
              sx={{
                textTransform: "none",
              }}
            >
              Close
            </Button>

            <Button
              variant="contained"
              disableElevation
              startIcon={
                <Icon
                  icon="lucide:pencil"
                  width={16}
                />
              }
              onClick={onEditRequest}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 2.5,
              }}
            >
              Edit guide
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={close}
              disabled={saving}
              sx={{
                textTransform: "none",
              }}
            >
              Cancel
            </Button>

            <SubmitButton
              loading={saving}
              onClick={submit}
            >
              {isEdit
                ? "Save changes"
                : "Create guide"}
            </SubmitButton>
          </>
        )
      }
    >
      {isPreview ? (
        /* ---------- PREVIEW ---------- */
        <Stack spacing={2.5}>
          <Stack
            spacing={1}
            alignItems="flex-start"
          >
            {optionName && (
              <Chip
                size="small"
                label={optionName}
                sx={{
                  color: t.accent,
                  backgroundColor:
                    t.accentBg,
                  fontWeight: 600,
                }}
              />
            )}

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: {
                  xs: 22,
                  sm: 26,
                },
                lineHeight: 1.3,
                color: t.text,
              }}
            >
              {form.title}
            </Typography>
          </Stack>

          {form.images.length > 0 && (
            <GuideImageStrip
              t={t}
              images={form.images}
              renderImage={(image) => (
                <Box
                  component="img"
                  src={image.preview}
                  alt={form.title}
                  sx={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "16 / 10",
                    objectFit: "cover",
                    borderRadius: 2,
                    border: t.bd,
                  }}
                />
              )}
            />
          )}

          {plainText ? (
            <Box
              className="ql-snow"
              sx={{
                "& .ql-editor": {
                  padding: 0,
                  fontSize: 15,
                  lineHeight: 1.8,
                  color: t.text,
                },

                "& .ql-editor a": {
                  color: t.accent,
                },

                "& .ql-editor blockquote": {
                  borderLeft: `4px solid ${t.accent}`,
                  color: t.muted,
                },

                "& .ql-editor pre": {
                  backgroundColor: t.subtle,
                  color: t.text,
                },
              }}
            >
              <div
                className="ql-editor"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    form.text,
                  ),
                }}
              />
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: t.muted,
              }}
            >
              This guide has no text.
            </Typography>
          )}
        </Stack>
      ) : (
        /* ---------- CREATE / EDIT ---------- */
        <Stack spacing={3}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            {/* Option */}
            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  mb: 0.7,
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Option
              </Typography>

              <FormControl
                size="small"
                fullWidth
              >
                <Select
                  value={form.optionId}
                  onChange={(e) =>
                    set(
                      "optionId",
                      e.target.value,
                    )
                  }
                  displayEmpty
                >
                  <MenuItem
                    value=""
                    disabled
                  >
                    Select an option
                  </MenuItem>

                  {options.map((o) => (
                    <MenuItem
                      key={o.id}
                      value={o.id}
                    >
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Guide Title */}
            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  mb: 0.7,
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  color: titleError
                    ? "error.main"
                    : "text.primary",
                }}
              >
                Guide title
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter guide title"
                value={form.title}
                onChange={(e) => {
                  set(
                    "title",
                    e.target.value,
                  );

                  if (titleError) {
                    setTitleError("");
                  }
                }}
                error={Boolean(titleError)}
                helperText={
                  titleError || " "
                }
                FormHelperTextProps={{
                  sx: {
                    ml: 0,
                    minHeight: "20px",
                  },
                }}
              />
            </Box>
          </Box>

          <Section
            t={t}
            title="Guide text"
          >
            <Box sx={quillSx}>
              <ReactQuill
                theme="snow"
                value={form.text}
                onChange={(value) =>
                  set("text", value)
                }
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write the steps or explanation here..."
              />
            </Box>
          </Section>

          <Section
            t={t}
            title="Images"
            hint="Select one or more PNG, JPG or WebP files"
          >
            {form.images.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <GuideImageStrip
                  t={t}
                  images={form.images}
                  renderImage={(image) => (
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      border: t.bd,
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={image.preview}
                      alt="Guide attachment"
                      sx={{
                        display: "block",
                        width: "100%",
                        aspectRatio: "16 / 10",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Remove image"
                      onClick={() => removeImage(image.id)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                      }}
                    >
                      <Icon icon="lucide:x" width={16} />
                    </IconButton>
                    {image.loading && (
                      <CircularProgress
                        size={20}
                        sx={{
                          position: "absolute",
                          left: "calc(50% - 10px)",
                          top: "calc(50% - 10px)",
                        }}
                      />
                    )}
                    {image.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", p: 0.5 }}
                      >
                        {image.error}
                      </Typography>
                    )}
                  </Box>
                  )}
                />
              </Box>
            )}

            <Box
              component="label"
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                takeFiles(e.dataTransfer.files);
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                py: 3.5,
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "center",
                border: `1.5px dashed ${dragging ? t.accent : t.border}`,
                backgroundColor: dragging ? t.accentBg : t.subtle,
                color: t.muted,
                "&:hover": { borderColor: t.accent },
              }}
            >
              <Icon icon="lucide:image-plus" width={26} color={t.accent} />
              <Typography
                variant="body2"
                sx={{ color: t.text, fontWeight: 500 }}
              >
                Drop images here or browse
              </Typography>
              <input
                hidden
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                onChange={(e) => {
                  takeFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Box>

            {form.imageUploadError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {form.imageUploadError}
              </Alert>
            )}
          </Section>
        </Stack>
      )}
    </ModalShell>
  );
};

const GuideDialog = (props) => {
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(
    props.open,
  );

  // Adjust state during render so the new
  // session is used before anything is painted.
  if (props.open !== wasOpen) {
    setWasOpen(props.open);

    if (props.open) {
      setSession((s) => s + 1);
    }
  }

  return (
    <GuideDialogInner
      key={`${props.guide?.id ?? "new"}-${session}`}
      {...props}
    />
  );
};

// ==============================================================
// DELETE OPTION MODAL
// ==============================================================
const DeleteOptionDialog = ({ t, open, option, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  if (!option) return null;

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BASE_URL}/guide/options/${option.id}`,
        getAuthConfig(),
      );
      await onDeleted();
      toast.success("Option deleted");
    } catch (e) {
      toast.error(errMsg(e, "Could not delete the option."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ModalShell
      t={t}
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="xs"
      icon="lucide:trash-2"
      title="Delete option"
      subtitle={option.name}
      footer={
        <>
          <Button
            onClick={onClose}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={deleting}
            onClick={confirmDelete}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon icon="lucide:trash-2" width={16} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
            }}
          >
            {deleting ? "Deleting..." : "Delete option"}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: t.text, lineHeight: 1.7 }}>
          Are you sure you want to delete <strong>{option.name}</strong>?
        </Typography>

        <Alert severity="warning">This action cannot be undone.</Alert>
      </Stack>
    </ModalShell>
  );
};

// ==============================================================
// DELETE GUIDE MODAL
// ==============================================================
const DeleteGuideDialog = ({ t, open, guide, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  if (!guide) return null;

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BASE_URL}/guide/guides/${guide.id}`,
        getAuthConfig(),
      );
      await onDeleted();
      toast.success("Guide deleted");
    } catch (e) {
      toast.error(errMsg(e, "Could not delete the guide."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ModalShell
      t={t}
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="xs"
      icon="lucide:trash-2"
      title="Delete guide"
      subtitle="This action cannot be undone"
      footer={
        <>
          <Button
            onClick={onClose}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={deleting}
            onClick={confirmDelete}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon icon="lucide:trash-2" width={16} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
            }}
          >
            {deleting ? "Deleting..." : "Delete guide"}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: t.text, lineHeight: 1.7 }}>
          Are you sure you want to delete this guide?
        </Typography>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: t.bd,
            backgroundColor: t.subtle,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: t.text }}>
            {guide.title}
          </Typography>
        </Box>

        <Alert severity="warning">This action cannot be undone.</Alert>
      </Stack>
    </ModalShell>
  );
};

// ==============================================================
// MAIN PAGE
// ==============================================================
const GuideBook = ({ isDarkMode = false }) => {
  const t = useMemo(() => getTokens(isDarkMode), [isDarkMode]);

  // ---------- roles (GET /guide/roles) ----------
  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState(""); // selected role_id
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [rolesError, setRolesError] = useState("");

  // ---------- options (GET /guide/options/{role_id}/roles) ----------
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [activeId, setActiveId] = useState(null);

  // ---------- guides (mock until the endpoints exist) ----------
  const [guides, setGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [guidesFor, setGuidesFor] = useState(null); // option id the current `guides` belong to
  const [guidesError, setGuidesError] = useState("");

  // ---------- ui state ----------
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [view, setView] = useState("grid");
  const [optionDialog, setOptionDialog] = useState({
    open: false,
    editing: null,
  });
  const [guideDialog, setGuideDialog] = useState({
    open: false,
    mode: "create",
    guide: null,
  });
  const [deleteOptionDialog, setDeleteOptionDialog] = useState({
    open: false,
    option: null,
  });
  const [deleteGuideDialog, setDeleteGuideDialog] = useState({
    open: false,
    guide: null,
  });

  // ---------- load roles ----------
  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    setRolesError("");
    try {
      const { data } = await axios.get(
        `${BASE_URL}/guide/roles`,
        getAuthConfig(),
      );
      const list = Array.isArray(data) ? data : [];
      setRoles(list);
      setRole((prev) => (prev !== "" ? prev : (list[0]?.role_id ?? "")));
    } catch (e) {
      setRolesError(errMsg(e, "Could not load roles."));
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  // ---------- load options ----------
  const optionsReq = useRef(0);
  const loadOptions = useCallback(async (roleId, { silent = false } = {}) => {
    if (roleId === "") return;
    const req = ++optionsReq.current;
    if (!silent) setLoadingOptions(true);
    setOptionsError("");
    try {
      let list = [];
      try {
        const { data } = await axios.get(
          `${BASE_URL}/guide/options/${roleId}/roles`,
          getAuthConfig(),
        );
        list = (Array.isArray(data) ? data : []).map((o) => ({
          id: o.id,
          name: o.option_name,
          description: o.description || "",
        }));
      } catch (e) {
        // the API answers 404 "No options found" when the table is empty
        if (e?.response?.status !== 404) throw e;
      }
      if (req !== optionsReq.current) return;
      setRoleOptions(list);
    } catch (e) {
      if (req !== optionsReq.current) return;
      setOptionsError(errMsg(e, "Could not load options."));
    } finally {
      if (req === optionsReq.current) setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (role === "") {
      setRoleOptions([]);
      setLoadingOptions(false);
      return;
    }
    setRoleOptions([]);
    setActiveId(null);
    loadOptions(role);
  }, [role, loadOptions]);

  // ---------- load guides ----------
  const guidesReq = useRef(0);
  const loadGuides = useCallback(
    async (optionId, { silent = false, searchTerm = "" } = {}) => {
      const req = ++guidesReq.current;
      if (!silent) setLoadingGuides(true);
      setGuidesError("");
      try {
        let data = [];
        try {
          const response = await axios.get(`${BASE_URL}/guide/guides`, {
            ...getAuthConfig(),
            params: {
              option_id: optionId,
              search: searchTerm || undefined,
            },
          });
          data = (Array.isArray(response.data) ? response.data : []).map(
            (guide) => ({
              id: guide.id,
              optionId: guide.option_id,
              title: guide.title,
              text: guide.text || "",
              images: Array.isArray(guide.image)
                ? guide.image
                : guide.image
                  ? [guide.image]
                  : [],
              updatedAt: guide.updated_at || guide.created_at,
            }),
          );
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }
        if (req !== guidesReq.current) return;
        setGuides(data);
      } catch (e) {
        if (req !== guidesReq.current) return;
        setGuidesError(errMsg(e, "Could not load guides."));
      } finally {
        if (req === guidesReq.current) {
          setGuidesFor(optionId);
          setLoadingGuides(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 1500);
    return () => clearTimeout(timeout);
  }, [search]);

  // ---------- derived ----------
  const pageLoading = loadingRoles || loadingOptions;
  const roleLabel = roles.find((r) => r.role_id === role)?.name || "";
  const active = roleOptions.find((o) => o.id === activeId) || null;
  const showGuideSkeleton =
    loadingGuides || (activeId != null && guidesFor !== activeId);

  // keep a valid option selected when the role / options change
  useEffect(() => {
    setActiveId((prev) =>
      roleOptions.some((o) => o.id === prev)
        ? prev
        : (roleOptions[0]?.id ?? null),
    );
  }, [roleOptions]);

  useEffect(() => {
    if (activeId == null) {
      guidesReq.current++;
      setGuides([]);
      setGuidesFor(null);
      setLoadingGuides(false);
      return;
    }
    loadGuides(activeId, { searchTerm: debouncedSearch });
  }, [activeId, debouncedSearch, loadGuides]);

  const visible = guides;

  const gridSx = {
    display: "grid",
    gap: 2.5,
    gridTemplateColumns:
      view === "grid" ? "repeat(auto-fill, minmax(320px, 1fr))" : "1fr",
  };

  // ---------- handlers ----------
  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
  };

  const retryAll = () => {
    loadRoles();
    if (role !== "") loadOptions(role);
  };

  const changeRole = (value) => {
    if (value === role) return;
    setRole(value);
    setRoleOptions([]);
    setActiveId(null);
    resetFilters();
  };

  const selectOption = (id) => {
    if (id === activeId) return;
    setLoadingGuides(true);
    setActiveId(id);
    resetFilters();
  };

  const afterOptionSave = async (optionId, created) => {
    if (created) resetFilters();
    await loadOptions(role, { silent: true });
    if (created && optionId != null) setActiveId(optionId);
    setOptionDialog({ open: false, editing: null });
  };

  const afterOptionDelete = async () => {
    await loadOptions(role, { silent: true });
    setDeleteOptionDialog({ open: false, option: null });
  };

  // guides
  const afterGuideChange = async (optionId) => {
    resetFilters();
    setGuideDialog((p) => ({ ...p, open: false }));
    if (optionId === activeId) {
      await loadGuides(activeId, { silent: true, searchTerm: "" });
    } else {
      setActiveId(optionId); // guides effect loads the new option
    }
  };

  const afterGuideDelete = async () => {
    setDeleteGuideDialog({ open: false, guide: null });
    await loadGuides(activeId, { silent: true, searchTerm: debouncedSearch });
  };

  const openGuideDialog = (dialogMode, guide = null) =>
    setGuideDialog({ open: true, mode: dialogMode, guide });

  const cardSx = {
    borderRadius: 3,
    boxShadow: "none",
    border: t.bd,
    backgroundColor: t.card,
  };

  // ---------- render ----------
  return (
    <Box
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: t.page, minHeight: "100vh" }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: t.text }}>
          Guide management
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: t.muted }}>
          Create role-based guides and organise them under options.
        </Typography>
      </Box>

      {/* ROLE + OPTIONS */}
      <Card
        sx={{
          ...cardSx,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            width: { xs: "auto", md: 280 },
            flexShrink: 0,
            borderRight: { md: t.bd },
            borderBottom: { xs: t.bd, md: "none" },
          }}
        >
          <Typography
            sx={{
              ...t.eyebrow,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Icon icon="lucide:users" width={14} /> Role context
          </Typography>

          {loadingRoles ? (
            <Skeleton variant="rounded" height={40} />
          ) : rolesError ? (
            <ErrorBanner message={rolesError} onRetry={loadRoles} />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={role}
                displayEmpty
                onChange={(e) => changeRole(e.target.value)}
                sx={{ fontWeight: 500 }}
                renderValue={(v) =>
                  roles.find((r) => r.role_id === v)?.name || "No roles found"
                }
              >
                {roles.map((r) => (
                  <MenuItem key={r.role_id} value={r.role_id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ p: 2.5, flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                ...t.eyebrow,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Icon icon="lucide:folder-open" width={14} /> Topic options
            </Typography>
            {roleOptions.length > 0 && !pageLoading && (
              <Typography variant="caption" sx={{ color: t.muted }}>
                Select an option to view or author guides
              </Typography>
            )}
          </Box>

          {pageLoading ? (
            <Stack direction="row" spacing={1}>
              {[110, 130, 120, 100].map((w, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={w}
                  height={34}
                  sx={{ borderRadius: 999 }}
                />
              ))}
            </Stack>
          ) : optionsError ? (
            <ErrorBanner
              message={optionsError}
              onRetry={() => loadOptions(role)}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              {roleOptions.length === 0 && (
                <Typography variant="body2" sx={{ color: t.muted, mr: 1 }}>
                  No options for {roleLabel || "this role"} yet.
                </Typography>
              )}

              {roleOptions.map((o) => {
                const on = o.id === activeId;
                return (
                  <Box
                    key={o.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectOption(o.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectOption(o.id);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pl: 1.75,
                      pr: on ? 0.5 : 1.25,
                      py: 0.5,
                      borderRadius: 999,
                      cursor: "pointer",
                      border: `1px solid ${on ? t.accent : t.border}`,
                      backgroundColor: on ? t.accentBg : "transparent",
                      color: on ? t.accent : t.text,
                      "&:hover": {
                        backgroundColor: on ? t.accentBg : t.subtle,
                      },
                      "&:focus-visible": { outline: `2px solid ${t.accent}` },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: on ? 600 : 500, color: "inherit" }}
                    >
                      {o.name}
                    </Typography>
                    {on && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderLeft: `1px solid ${t.accent}55`,
                          ml: 0.25,
                          pl: 0.25,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label="Edit option"
                          sx={{ color: "inherit" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOptionDialog({ open: true, editing: o });
                          }}
                        >
                          <Icon icon="lucide:pencil" width={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete option"
                          sx={{ color: "inherit" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteOptionDialog({ open: true, option: o });
                          }}
                        >
                          <Icon icon="lucide:trash-2" width={14} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}

              <Button
                size="small"
                disabled={role === ""}
                startIcon={<Icon icon="lucide:plus" width={15} />}
                onClick={() => setOptionDialog({ open: true, editing: null })}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  border: `1px dashed ${t.muted}`,
                  color: t.muted,
                  px: 1.75,
                }}
              >
                Add option
              </Button>
            </Box>
          )}
        </Box>
      </Card>

      {/* SELECTED OPTION */}
      <Card sx={cardSx}>
        {pageLoading ? (
          <>
            <Box sx={{ p: 3 }}>
              <Skeleton width={140} height={16} />
              <Skeleton width={260} height={38} sx={{ mt: 0.5 }} />
              <Skeleton width={340} height={20} />
            </Box>
            <Box sx={{ p: 3, borderTop: t.bd, ...gridSx }}>
              {[0, 1, 2].map((i) => (
                <GuideCardSkeleton key={i} t={t} view={view} />
              ))}
            </Box>
          </>
        ) : rolesError || optionsError ? (
          <EmptyState
            t={t}
            icon="lucide:cloud-off"
            title="Something went wrong"
            hint={rolesError || optionsError}
            action={
              <Button
                onClick={retryAll}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Try again
              </Button>
            }
          />
        ) : !active ? (
          role === "" ? (
            <EmptyState
              t={t}
              icon="lucide:users"
              title="No roles found"
              hint="Roles need to exist before options and guides can be added."
            />
          ) : (
            <EmptyState
              t={t}
              icon="lucide:folder-plus"
              title={`No options for ${roleLabel}`}
              hint="Add an option first, then create guides under it."
              action={
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Icon icon="lucide:plus" width={18} />}
                  onClick={() => setOptionDialog({ open: true, editing: null })}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Add option
                </Button>
              }
            />
          )
        ) : (
          <>
            {/* header */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...t.eyebrow, color: t.accent }}>
                  {roleLabel} option
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ mt: 1, fontWeight: 700, color: t.text }}
                >
                  {active.name}
                </Typography>
                {active.description && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: t.muted }}>
                    {active.description}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                disableElevation
                startIcon={<Icon icon="lucide:plus" width={18} />}
                onClick={() => openGuideDialog("create")}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.1,
                }}
              >
                Create guide
              </Button>
            </Box>

            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderTop: t.bd,
                borderBottom: t.bd,
              }}
            >
              <TextField
                size="small"
                placeholder={`Search guides in "${active.name}"...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: { xs: "100%", sm: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="lucide:search" width={17} />
                    </InputAdornment>
                  ),
                }}
              />

              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_, v) => v && setView(v)}
              >
                <ToggleButton value="grid" aria-label="Grid view">
                  <Icon icon="lucide:layout-grid" width={16} />
                </ToggleButton>
                <ToggleButton value="list" aria-label="List view">
                  <Icon icon="lucide:list" width={16} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* body */}
            {showGuideSkeleton ? (
              <Box sx={{ p: 3, borderTop: t.bd, ...gridSx }}>
                {[0, 1, 2].map((i) => (
                  <GuideCardSkeleton key={i} t={t} view={view} />
                ))}
              </Box>
            ) : guidesError ? (
              <Box sx={{ p: 3, borderTop: t.bd }}>
                <ErrorBanner
                  message={guidesError}
                  onRetry={() =>
                    loadGuides(activeId, { searchTerm: debouncedSearch })
                  }
                />
              </Box>
            ) : guides.length === 0 ? (
              <Box>
                <EmptyState
                  t={t}
                  icon="lucide:search-x"
                  title="No guides found"
                  hint="No guides are available to display for this option."
                />
              </Box>
            ) : (
              <>
                <Box sx={{ p: 3 }}>
                  {visible.length === 0 ? (
                    <EmptyState
                      t={t}
                      icon="lucide:search-x"
                      title="No guides match"
                      hint="Try a different search term."
                      action={
                        <Button
                          onClick={resetFilters}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Clear search
                        </Button>
                      }
                    />
                  ) : (
                    <Box sx={gridSx}>
                      {visible.map((g) => (
                        <GuideCard
                          key={g.id}
                          t={t}
                          guide={g}
                          view={view}
                          onPreview={(guide) =>
                            openGuideDialog("preview", guide)
                          }
                          onEdit={(guide) => openGuideDialog("edit", guide)}
                          onDelete={(id) =>
                            setDeleteGuideDialog({
                              open: true,
                              guide: guides.find((x) => x.id === id) || null,
                            })
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Card>

      {/* MODALS */}
      <OptionDialog
        t={t}
        open={optionDialog.open}
        editing={optionDialog.editing}
        roleLabel={roleLabel}
        roleId={role}
        onClose={() => setOptionDialog((p) => ({ ...p, open: false }))}
        onSaved={afterOptionSave}
      />

      <DeleteOptionDialog
        t={t}
        open={deleteOptionDialog.open}
        option={deleteOptionDialog.option}
        onClose={() => setDeleteOptionDialog({ open: false, option: null })}
        onDeleted={afterOptionDelete}
      />

      <GuideDialog
        t={t}
        open={guideDialog.open}
        mode={guideDialog.mode}
        guide={guideDialog.guide}
        options={roleOptions}
        defaultOptionId={activeId}
        onSaved={afterGuideChange}
        onEditRequest={() => setGuideDialog((p) => ({ ...p, mode: "edit" }))}
        onClose={() => setGuideDialog((p) => ({ ...p, open: false }))}
      />

      <DeleteGuideDialog
        t={t}
        open={deleteGuideDialog.open}
        guide={deleteGuideDialog.guide}
        onClose={() => setDeleteGuideDialog({ open: false, guide: null })}
        onDeleted={afterGuideDelete}
      />
    </Box>
  );
};

export default GuideBook;
